// src/pages/access/InviteUserForm.tsx — Phase 9 c10 (admin invite flow).
//
// Modal called from AccessUsersPage. POSTs /api/access/invitations.
//
// Client-side checks:
//   - email format
//   - @nymr.co.uk → friendly inline reject ("staff use SSO; this flow is for
//     non-staff PIN users only"). Mirrors SSO_DOMAIN server-side rejection
//     so the user sees a clear message before the round trip.
//
// Server errors we surface specifically:
//   - SSO_DOMAIN (400)            — restated client-side, never reached in
//                                   normal flow but defence-in-depth
//   - EMAIL_EXISTS (409)          — message + jump to the existing record
//   - generic 5xx / network       — show plain error message
//
// On success the modal closes and the parent receives an onSent() callback
// with the new user shape so it can fold the new user into its list.

import { useState } from "react";
import { apiFetch, ApiError } from "../../api";
import { C, F } from "../../theme";
import { Button, Card, Eyebrow, Field } from "../../components/ui";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const NYMR_DOMAIN_SUFFIX = "@nymr.co.uk";

interface InviteResponse {
  ok: true;
  id: number;
  email: string;
  signOnStatus: "pending";
  inviteExpiresAt: string;
  emailed: boolean;
}

interface InviteUserFormProps {
  onClose: () => void;
  onSent: (result: InviteResponse) => void;
  onJumpToExisting?: (userId: number) => void;
}

export function InviteUserForm({ onClose, onSent, onJumpToExisting }: InviteUserFormProps) {
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [existingUserId, setExistingUserId] = useState<number | null>(null);

  async function submit() {
    setError(null);
    setExistingUserId(null);

    const trimmed = email.trim().toLowerCase();
    if (!EMAIL_REGEX.test(trimmed)) {
      setError("Enter a valid email address.");
      return;
    }
    if (trimmed.endsWith(NYMR_DOMAIN_SUFFIX)) {
      setError(
        "NYMR staff (@nymr.co.uk) sign in via Microsoft. They don't need a PIN invitation — they can just visit the site and tap Sign In.",
      );
      return;
    }

    setSending(true);
    try {
      const result = await apiFetch<InviteResponse>("/api/access/invitations", {
        method: "POST",
        body: JSON.stringify({
          email: trimmed,
          first_name: firstName.trim(),
          last_name: lastName.trim(),
        }),
      });
      onSent(result);
    } catch (e) {
      if (e instanceof ApiError && typeof e.body === "object" && e.body !== null) {
        const b = e.body as { error?: string; message?: string; userId?: number };
        if (b.error === "SSO_DOMAIN") {
          setError(
            b.message ??
              "NYMR staff (@nymr.co.uk) sign in via Microsoft. They don't need a PIN invitation.",
          );
        } else if (b.error === "EMAIL_EXISTS") {
          setError(b.message ?? "A user with this email already exists.");
          if (typeof b.userId === "number") setExistingUserId(b.userId);
        } else {
          setError(b.message ?? e.message);
        }
      } else {
        setError(e instanceof ApiError ? e.message : "Couldn't send invitation.");
      }
    } finally {
      setSending(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(43,32,20,0.55)",
        zIndex: 100,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 460,
          maxHeight: "85vh",
          overflowY: "auto",
        }}
      >
        <Card>
          <Eyebrow>Invite to Hub</Eyebrow>
          <h2
            style={{
              fontFamily: F.serif,
              fontSize: 22,
              fontWeight: 700,
              color: C.ink,
              margin: "6px 0 4px",
              letterSpacing: -0.3,
            }}
          >
            Send a login link with a generated PIN
          </h2>
          <div
            style={{
              fontFamily: F.serif,
              fontStyle: "italic",
              fontSize: 13,
              color: C.inkSoft,
              lineHeight: 1.45,
              marginBottom: 14,
            }}
          >
            We'll generate a PIN and email them a login link valid for 7 days. They can change
            the PIN once signed in.
          </div>

          <Field
            label="Email address"
            value={email}
            onChange={setEmail}
            type="email"
            ph="name@example.co.uk"
          />
          <Field
            label="First name (optional)"
            value={firstName}
            onChange={setFirstName}
          />
          <Field
            label="Last name (optional)"
            value={lastName}
            onChange={setLastName}
          />

          {error && (
            <div
              style={{
                padding: 12,
                borderRadius: 10,
                background: C.signalBg,
                border: `1px solid rgba(184,58,43,0.22)`,
                color: C.signalDk,
                fontFamily: F.body,
                fontWeight: 700,
                fontSize: 13,
                marginTop: 10,
              }}
            >
              {error}
              {existingUserId !== null && onJumpToExisting && (
                <div style={{ marginTop: 8 }}>
                  <Button
                    variant="secondary"
                    onClick={() => onJumpToExisting(existingUserId)}
                  >
                    Open existing record
                  </Button>
                </div>
              )}
            </div>
          )}

          <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
            <Button variant="ghost" fullWidth onClick={onClose} disabled={sending}>
              Cancel
            </Button>
            <Button variant="primary" fullWidth onClick={submit} disabled={sending}>
              {sending ? "Sending…" : "Send invitation"}
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}

export type { InviteResponse };
